import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { Subscription } from 'rxjs';
import { HttpServiceService } from '../services/http-service.service';
import { NgxToastrService } from '../_services/ngx-toastr/ngx-toastr.service';
import { BreadcrumbComponent } from '../elements/breadcrumb/breadcrumb.component';

interface BillCategory {
  id: number;
  bill_type: number;
  name: string;
  enable_notification: boolean;
  owner_notification: boolean;
  tenant_notification: boolean;
}

@Component({
  selector: 'app-bill-categories',
  standalone: true,
  imports: [ReactiveFormsModule, NgFor, NgIf, NgClass, BreadcrumbComponent],
  templateUrl: './bill-categories.component.html',
  styleUrl: './bill-categories.component.css',
})
export class BillCategoriesComponent implements OnInit, OnDestroy {
  breadcrumbList = {
    breadcrumb_path: 'Dashboard',
    currentURL: 'Bill Categories',
    breadcrumb_url: '/admin',
  };

  billTypes = [
    { key: 1, label: 'Rent / Security Deposit' },
    { key: 2, label: 'Expenses' },
    { key: 3, label: 'Company Fee' },
    { key: 4, label: 'Other' },
  ];

  categories: BillCategory[] = [];
  filteredCategories: BillCategory[] = [];
  selectedBillType: number | null = null;
  isLoading = false;

  showModal = false;
  isEditMode = false;
  editingId: number | null = null;
  categoryForm!: FormGroup;

  private httpService = inject(HttpServiceService);
  private alertService = inject(NgxToastrService);
  private fb = inject(FormBuilder);

  private listSub?: Subscription;
  private saveSub?: Subscription;
  private deleteSub?: Subscription;

  ngOnInit(): void {
    this.initForm();
    this.loadCategories();
  }

  initForm(): void {
    this.categoryForm = this.fb.group({
      bill_type: [null, Validators.required],
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(40)]],
      enable_notification: [false],
      owner_notification: [false],
      tenant_notification: [false],
    });

    // Auto-disable sub-flags when enable_notification is turned off
    this.categoryForm.get('enable_notification')?.valueChanges.subscribe((enabled: boolean) => {
      if (!enabled) {
        this.categoryForm.patchValue({ owner_notification: false, tenant_notification: false }, { emitEvent: false });
      }
    });
  }

  loadCategories(): void {
    this.isLoading = true;
    this.listSub = this.httpService
      .getAdminBillCategories(this.selectedBillType ?? undefined)
      .subscribe({
        next: (res) => {
          this.categories = res.data ?? [];
          this.applyFilter();
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  applyFilter(): void {
    if (this.selectedBillType) {
      this.filteredCategories = this.categories.filter(
        (c) => c.bill_type === this.selectedBillType
      );
    } else {
      this.filteredCategories = [...this.categories];
    }
  }

  onBillTypeFilter(event: Event): void {
    const val = (event.target as HTMLSelectElement).value;
    this.selectedBillType = val ? Number(val) : null;
    this.applyFilter();
  }

  getBillTypeLabel(key: number): string {
    return this.billTypes.find((t) => t.key === key)?.label ?? String(key);
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.editingId = null;
    this.categoryForm.reset({ enable_notification: false, owner_notification: false, tenant_notification: false });
    this.showModal = true;
  }

  openEditModal(category: BillCategory): void {
    this.isEditMode = true;
    this.editingId = category.id;
    this.categoryForm.patchValue({
      bill_type: category.bill_type,
      name: category.name,
      enable_notification: category.enable_notification,
      owner_notification: category.owner_notification,
      tenant_notification: category.tenant_notification,
    });
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
    this.categoryForm.reset({ enable_notification: false, owner_notification: false, tenant_notification: false });
  }

  saveCategory(): void {
    if (this.categoryForm.invalid) {
      this.categoryForm.markAllAsTouched();
      return;
    }
    const payload = this.categoryForm.value;

    if (this.isEditMode && this.editingId !== null) {
      this.saveSub = this.httpService.updateAdminBillCategory(this.editingId, payload).subscribe({
        next: (res) => {
          this.alertService.success(res.message ?? 'Category updated.', 'toast-top-right');
          this.closeModal();
          this.loadCategories();
        },
      });
    } else {
      this.saveSub = this.httpService.createAdminBillCategory(payload).subscribe({
        next: (res) => {
          this.alertService.success(res.message ?? 'Category created.', 'toast-top-right');
          this.closeModal();
          this.loadCategories();
        },
      });
    }
  }

  deleteCategory(category: BillCategory): void {
    if (!confirm(`Delete category "${category.name}"? This cannot be undone.`)) return;
    this.deleteSub = this.httpService.deleteAdminBillCategory(category.id).subscribe({
      next: (res) => {
        this.alertService.success(res.message ?? 'Category deleted.', 'toast-top-right');
        this.loadCategories();
      },
    });
  }

  isNotificationEnabled(): boolean {
    return this.categoryForm.get('enable_notification')?.value === true;
  }

  ngOnDestroy(): void {
    this.listSub?.unsubscribe();
    this.saveSub?.unsubscribe();
    this.deleteSub?.unsubscribe();
  }
}
